import { FunctionComponent } from 'react';

import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { AutoPanelLayoutTestPage } from 'core/ui/AutoPanelLayout';

import { MOSAiCRouteWrapper } from 'features/MOSAiC/components/MOSAiCRouteWrapper';
import { AccountForm } from 'features/account/fragments/AccountForm';
import { AccountHome } from 'features/account/fragments/AccountHome';
import { RouteGuard, UserRoles } from 'features/auth';
import { AuthenticationScheme } from 'features/auth/constants';
import { OidcSignIn } from 'features/auth/fragments/OidcSignIn';
import { OidcSignOut } from 'features/auth/fragments/OidcSignOut';
import { SignOut } from 'features/auth/fragments/SignOut';
import { DestinationRouteForm } from 'features/destination-route/fragments/DestinationRouteForm';
import { DestinationRouteHome } from 'features/destination-route/fragments/DestinationRouteHome';
import { DestinationForm } from 'features/destination/fragments/DestinationForm';
import { DestinationHome } from 'features/destination/fragments/DestinationHome';
import { Approve } from 'features/exam-reading/fragments/Approve';
import { ExamRead } from 'features/exam-reading/fragments/ExamRead';
import { ExamHome } from 'features/exam/fragments/ExamHome';
import { ExamProcessing } from 'features/exam/fragments/ExamProcessing';
import { BasicUploadViewProvider } from 'features/file/fragments/BasicUploadViewProvider';
import { FileViewerPage } from 'features/file/fragments/FileViewerPage';
import { AddUpdateForm } from 'features/form/fragments/AddUpdateForm';
import { FormGrid } from 'features/form/fragments/FormGrid';
import { GroupForm } from 'features/group/fragments/GroupForm';
import { GroupHome } from 'features/group/fragments/GroupHome';
import { HL7SendHome } from 'features/hl7send/fragments/HL7SendHome';
import { Home } from 'features/home';
import { IntegrationEdit } from 'features/integration/fragments/IntegrationEdit';
import { IntegrationHome } from 'features/integration/fragments/IntegrationHome';
import { LocationFinalReportTemplateForm } from 'features/location-finalreport-template/fragments/LocationFinalReportTemplateForm';
import { LocationFinalReportTemplateHome } from 'features/location-finalreport-template/fragments/LocationFinalReportTemplateHome';
import { LocationTypeForm } from 'features/location-type/fragments/LocationTypeForm';
import { LocationTypeHome } from 'features/location-type/fragments/LocationTypeHome';
import { LocationForm } from 'features/location/fragments/LocationForm';
import { LocationHome } from 'features/location/fragments/LocationHome';
import { PACSHome } from 'features/pacs/fragments/PACSHome';
import { PatientAdd } from 'features/patient/fragments/PatientAdd';
import { PatientEdit } from 'features/patient/fragments/PatientEdit';
import { PatientHome } from 'features/patient/fragments/PatientHome';
import { PatientHomeInit } from 'features/patient/fragments/PatientHomeInit';
import { PhysicianDashboardPage } from 'features/physician-dashboard/fragments/PhysicianDashboardPage';
import { PhysicianForm } from 'features/physician/fragments/PhysicianForm';
import { PhysicianHome } from 'features/physician/fragments/PhysicianHome';
import { QueryRetrieveHome } from 'features/query-retrieve/fragments/QueryRetrieveHome';
import { ReadRequestFormEdit } from 'features/read-request';
import { SendQueue } from 'features/send-queue';
import { ServiceForm } from 'features/service/fragments/ServiceForm';
import { ServiceHome } from 'features/service/fragments/ServiceHome';
import { PortalSettingsPage } from 'features/settings/fragments/PortalSettingsPage';
import { MobileFilters } from 'features/share/fragments/MobileFilters';
import { OnePage } from 'features/share/fragments/OnePage';
import { ShareDefaultAuthorizationChallenge } from 'features/share/fragments/ShareDefaultAuthorizationChallenge';
import { ShareFileViewerPage } from 'features/share/fragments/ShareFileViewerPage';
import { ShareHome } from 'features/share/fragments/ShareHome';
import { ShareInit } from 'features/share/fragments/ShareInit';
import { ShareLanding } from 'features/share/fragments/ShareLanding';
import { ShareLandingAuthorizationChallenge } from 'features/share/fragments/ShareLandingAuthorizationChallenge';
import { ShareUploadAuthorizationChallenge } from 'features/share/fragments/ShareUploadAuthorizationChallenge';
import { Viewer } from 'features/share/fragments/Viewer';
import { SourceForm } from 'features/source/fragments/SourceForm';
import { SourceHome } from 'features/source/fragments/SourceHome';
import { AddTagsFieldTemplatePage } from 'features/tags/fragments/AddTagsFieldTemplatePage';
import { EditTagsFieldTemplatePage } from 'features/tags/fragments/EditTagsFieldTemplatePage';
import { TagsFieldTemplateGrid } from 'features/tags/fragments/TagsFieldTemplateGrid';
import { TemplateForm } from 'features/template/fragments/TemplateForm';
import { TemplateHome } from 'features/template/fragments/TemplateHome';
import { UnitForm } from 'features/unit/fragments/UnitForm';
import { UnitHome } from 'features/unit/fragments/UnitHome';
import { UploadAlternativePage } from 'features/upload-alternative/fragments/UploadAlternativePage';
import { UploadExamsInit } from 'features/upload-exams/fragments/UploadExamsInit';
import { UploadExamsPage } from 'features/upload-exams/fragments/UploadExamsPage';
import { UploadViewProvider } from 'features/upload-exams/fragments/UploadViewProvider';
import { UploadPipelineProvider } from 'features/upload-pipeline/fragments/UploadPipelineProvider';
import { UploadTrackerHome } from 'features/upload-tracker/fragments/UploadTrackerHome';
import { UserForm } from 'features/user/fragments/UserForm';
import { UserHome } from 'features/user/fragments/UserHome';
import { WorklistManagementPage } from 'features/worklist/pages/WorklistManagementPage';
import { WorklistViewUserForm } from 'features/worklistviewuser/fragments/WorklistViewUserForm';
import { WorklistViewUserGridView } from 'features/worklistviewuser/fragments/WorklistViewUserGridView';

import { NAVIGATION_LINK_IDS, SHARE_BASE_ROUTE_ID } from '../constants';
import { MainRoutes } from '../routes';
import { App } from './App';
import { AppProvider } from './AppProvider';

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AppProvider>
        <App />
      </AppProvider>
    ),
    children: [
      {
        path: MainRoutes.AUTH,
        element: <Outlet />,
        children: [
          { path: 'signout', element: <SignOut /> },
          { path: 'oidc-signout', element: <OidcSignOut /> },
          { path: 'oidc-signin', element: <OidcSignIn /> },
        ],
      },
      {
        path: MainRoutes.EXAM,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.EXAMS.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer Admin', 'Customer']}>
                <ExamHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer Admin', 'Customer']}>
                <ExamProcessing />
              </RouteGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer Admin', 'Customer']}>
                <ExamProcessing />
              </RouteGuard>
            ),
          },
          {
            path: ':id/read/approve',
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer Admin', 'Physician']}>
                <Approve />
              </RouteGuard>
            ),
          },
          {
            path: ':id/read',
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer Admin', 'Physician']}>
                <ExamRead />
              </RouteGuard>
            ),
          },
          {
            path: ':id/read-request',
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer Admin', 'Physician', 'Customer']}>
                <ReadRequestFormEdit />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.LOCATION,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.LOCATIONS.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <LocationHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <LocationForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <LocationForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.LOCATION_TYPE,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.LOCATION_TYPES.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <LocationTypeHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <LocationTypeForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <LocationTypeForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.PHYSICIAN,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.PHYSICIANS.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <PhysicianHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <PhysicianForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <PhysicianForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.PHYSICIAN_DASHBOARD,
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Physician']}>
                <PhysicianDashboardPage />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.UNIT,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.DEVICES.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <UnitHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <UnitForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <UnitForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.GROUP,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.GROUPS.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <GroupHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <GroupForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <GroupForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.UPLOAD_TRACKER,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.UPLOAD_TRACKER.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <UploadTrackerHome />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.UPLOAD_TOOL,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.UPLOAD_EXAM.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer', 'Customer Admin', 'Support']} initComponent={UploadExamsInit}>
                <UploadPipelineProvider mode="interactive" trackerSource="Web-UploadExamsPage">
                  <UploadViewProvider>
                    <UploadExamsPage />
                  </UploadViewProvider>
                </UploadPipelineProvider>
              </RouteGuard>
            ),
          },
          {
            path: 'patient/:patientId',
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer', 'Customer Admin', 'Support']} initComponent={UploadExamsInit}>
                <UploadPipelineProvider mode="interactive" trackerSource="Web-UploadExamsPage">
                  <UploadViewProvider>
                    <UploadExamsPage />
                  </UploadViewProvider>
                </UploadPipelineProvider>
              </RouteGuard>
            ),
          },
          {
            path: 'patient/:patientId/exam/:examId',
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer', 'Customer Admin', 'Support']} initComponent={UploadExamsInit}>
                <UploadPipelineProvider mode="interactive" trackerSource="Web-UploadExamsPage">
                  <UploadViewProvider>
                    <UploadExamsPage />
                  </UploadViewProvider>
                </UploadPipelineProvider>
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.UPLOAD_ALTERNATIVE,
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer', 'Customer Admin', 'Support']}>
                <UploadPipelineProvider trackerSource="Web-Alternative">
                  <BasicUploadViewProvider>
                    <UploadAlternativePage />
                  </BasicUploadViewProvider>
                </UploadPipelineProvider>
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.SHARE_EXAM,
        element: (
          <RouteGuard allowedRoles={['ExamShare']}>
            <Outlet />
          </RouteGuard>
        ),
        children: [
          { index: true, element: <UploadExamsPage /> },
          { path: 'patient/:patientId', element: <UploadExamsPage /> },
          { path: 'patient/:patientId/exam/:examId', element: <UploadExamsPage /> },
        ],
      },
      {
        path: MainRoutes.USER,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.USERS.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <UserHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <UserForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <UserForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.ACCOUNT,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.ACCOUNTS.toString(),
            element: (
              <RouteGuard allowedRoles={[UserRoles.ADMIN, UserRoles.CUSTOMER_ADMIN]}>
                <AccountHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={[UserRoles.ADMIN, UserRoles.CUSTOMER_ADMIN]}>
                <AccountForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={[UserRoles.ADMIN, UserRoles.CUSTOMER_ADMIN]}>
                <AccountForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.PATIENT,
        element: (
          <RouteGuard allowedRoles={[UserRoles.CUSTOMER_ADMIN, UserRoles.CUSTOMER, UserRoles.ADMIN, UserRoles.PHYSICIAN]}>
            <Outlet />
          </RouteGuard>
        ),
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.PATIENTS.toString(),
            element: <PatientHomeInit />,
          },
          {
            path: 'home',
            element: <PatientHome />,
          },
          {
            path: 'add',
            element: <PatientAdd />,
          },

          {
            path: 'edit/:id',
            element: <PatientEdit isEditMode />,
            children: [
              { index: true, element: <Navigate to="exams" replace /> },
              { path: 'cases', element: <PatientEdit isEditMode /> },
              { path: 'files/*', element: <MOSAiCRouteWrapper /> },
              { path: 'dashboard', element: <PatientEdit isEditMode /> },
              { path: 'exams', element: <PatientEdit isEditMode /> },
            ],
          },
        ],
      },
      {
        path: MainRoutes.PACS,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.PACS.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <PACSHome />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.DESTINATION,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.DESTINATIONS.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <DestinationHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={DestinationHome}>
                <DestinationForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={DestinationHome}>
                <DestinationForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.SOURCE,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.SOURCES.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <SourceHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={SourceHome}>
                <SourceForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={SourceHome}>
                <SourceForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.SERVICE,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.SERVICE.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <ServiceHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={ServiceHome}>
                <ServiceForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={ServiceHome}>
                <ServiceForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.HL7SEND,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.HL7SEND.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <HL7SendHome />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.INTEGRATION,
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <IntegrationHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <IntegrationEdit />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <IntegrationEdit />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.FORM,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.FORM.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <FormGrid />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={FormGrid}>
                <AddUpdateForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={FormGrid}>
                <AddUpdateForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.TEMPLATE,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.TEMPLATES.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <TemplateHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={TemplateHome}>
                <TemplateForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={TemplateHome}>
                <TemplateForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.SETTINGS,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.PORTAL_SETTINGS.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin>
                <PortalSettingsPage />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.WORKLIST_VIEW_USER,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.WORKLIST_VIEW_USER.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <WorklistViewUserGridView />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <WorklistViewUserForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <WorklistViewUserForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.WORKLIST_MANAGEMENT,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.WORKLIST_MANAGEMENT.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <WorklistManagementPage />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.SHARE,
        id: SHARE_BASE_ROUTE_ID,
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareDefaultAuthorizationChallenge} initComponent={ShareInit}>
                <ShareHome />
              </RouteGuard>
            ),
          },
          {
            path: 'onepage',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareDefaultAuthorizationChallenge} initComponent={ShareInit}>
                <OnePage>
                  <div>One Page View Content</div>
                </OnePage>
              </RouteGuard>
            ),
          },
          {
            path: 'file-viewer/:examId',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareDefaultAuthorizationChallenge} initComponent={ShareInit}>
                <ShareFileViewerPage />
              </RouteGuard>
            ),
          },
          {
            path: 'landing',
            element: (
              <RouteGuard allowedSchemes={[AuthenticationScheme.ANONYMOUS, AuthenticationScheme.OIDC]} challengeComponent={ShareLandingAuthorizationChallenge}>
                <ShareLanding />
              </RouteGuard>
            ),
          },
          {
            path: 'viewer/*',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareDefaultAuthorizationChallenge} initComponent={ShareInit}>
                <Viewer />
              </RouteGuard>
            ),
            children: [{ path: 'filters', element: <MobileFilters /> }],
          },
          {
            path: 'upload',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareUploadAuthorizationChallenge} initComponent={ShareInit}>
                <ShareHome />
              </RouteGuard>
            ),
          },
          {
            path: 'upload/*',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareUploadAuthorizationChallenge} initComponent={ShareInit}>
                <ShareHome />
              </RouteGuard>
            ),
          },
          {
            path: 'insight',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareDefaultAuthorizationChallenge} initComponent={ShareInit}>
                <ShareHome />
              </RouteGuard>
            ),
          },
          {
            path: 'insight/*',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareDefaultAuthorizationChallenge} initComponent={ShareInit}>
                <ShareHome />
              </RouteGuard>
            ),
          },
          {
            path: 'view/*',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareDefaultAuthorizationChallenge} initComponent={ShareInit}>
                <ShareHome />
              </RouteGuard>
            ),
          },
          {
            path: 'files',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareDefaultAuthorizationChallenge} initComponent={ShareInit}>
                <ShareHome />
              </RouteGuard>
            ),
          },
          {
            path: 'files/*',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareDefaultAuthorizationChallenge} initComponent={ShareInit}>
                <ShareHome />
              </RouteGuard>
            ),
          },
          {
            path: 'exams',
            element: (
              <RouteGuard allowedSchemes={AuthenticationScheme.SHARE} challengeComponent={ShareDefaultAuthorizationChallenge} initComponent={ShareInit}>
                <ShareHome />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.DESTINATION_ROUTE,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.DESTINATION_ROUTE.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <DestinationRouteHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={DestinationRouteHome}>
                <DestinationRouteForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={DestinationRouteHome}>
                <DestinationRouteForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.LOCATIONFINALREPORT_TEMPLATE,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.LOCATIONFINALREPORT_TEMPLATE.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <LocationFinalReportTemplateHome />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={LocationFinalReportTemplateHome}>
                <LocationFinalReportTemplateForm />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={LocationFinalReportTemplateHome}>
                <LocationFinalReportTemplateForm />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.QUERY_RETRIEVE,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.QUERY_RETRIEVE.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Customer Admin', 'Customer']}>
                <QueryRetrieveHome />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.FILE_VIEWER,
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <RouteGuard allowedRoles={['Administrator', 'Support', 'Physician', 'Customer Admin', 'ExamShare', 'Customer']}>
                <FileViewerPage />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.TAGS,
        element: <Outlet />,
        children: [
          {
            index: true,
            id: NAVIGATION_LINK_IDS.TAGS.toString(),
            element: (
              <RouteGuard allowedRoles={['Administrator']}>
                <TagsFieldTemplateGrid />
              </RouteGuard>
            ),
          },
          {
            path: 'add',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={TagsFieldTemplateGrid}>
                <AddTagsFieldTemplatePage />
              </RouteGuard>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <RouteGuard allowedRoles={['Administrator']} requireSystemAdmin challengeComponent={TagsFieldTemplateGrid}>
                <EditTagsFieldTemplatePage />
              </RouteGuard>
            ),
          },
        ],
      },
      {
        path: MainRoutes.SEND_QUEUE,
        id: NAVIGATION_LINK_IDS.SEND_QUEUE.toString(),
        element: (
          <RouteGuard allowedRoles={UserRoles.ADMIN}>
            <SendQueue />
          </RouteGuard>
        ),
      },
      {
        path: MainRoutes.HOME,
        id: NAVIGATION_LINK_IDS.HOME.toString(),
        element: (
          <RouteGuard>
            <Home />
          </RouteGuard>
        ),
      },
      {
        path: MainRoutes.HOME_2,
        element: (
          <RouteGuard>
            <Home />
          </RouteGuard>
        ),
      },
      {
        path: 'auto-panel-layout-test',
        element: (
          <RouteGuard>
            <AutoPanelLayoutTestPage />
          </RouteGuard>
        ),
      },
      { element: <Navigate to={MainRoutes.HOME} replace /> },
    ],
  },
]);

export const AppRouter: FunctionComponent = () => <RouterProvider router={router} />;

AppRouter.displayName = 'AppRouter';
